import { Injectable } from '@angular/core'
import { LocalMediaFile, LogService, Media } from 'htv-lib'
import { WebOSIDCAP } from './webos-idcap'
import { WebOSHCAP } from './webos-hcap'
import { MonitoringMemoryData, MonitoringStorageData } from '../interfaces/monitoring'
import { PowerState } from '../interfaces/power-state'
import { NetworkData, NetworkState } from '../interfaces/network'
import { ChannelPosition } from '../pages/channel/channel-position'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { BehaviorSubject } from 'rxjs'
import { ExternalInputData } from '../services/external-input.service'
import { KEYS } from './webos-keys'
import { HTVConfig, WebOSInstantOn } from '../interfaces/htv-config'

@Injectable({ providedIn: 'root' })
export class WebOSInternal {
  private readonly MEDIA_DIR = 'file://internal/medias'
  private readonly MEDIA_PATH = 'http://127.0.0.1:8151/__internal__/medias'
  private USE_IDCAP = true

  private _idcap: WebOSIDCAP
  private _hcap: WebOSHCAP

  constructor(private logService: LogService) {
    this._idcap = new WebOSIDCAP(logService)
    this._hcap = new WebOSHCAP(logService)
  }

  //https://procentric.developer.lge.com/api-references/idcap-api/system#key-code-list
  registerRemoteKeys(): void {
    this.logService.info(`[WebOS] Registering remote keys`)

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(KEYS).map(([name, code]) => {
      this._idcap.registerKey(0, name)
    })
  }

  async initStorage(): Promise<void> {
    try {
      const exist = await this._idcap.fileExist(this.MEDIA_DIR)
      if (!exist) {
        await this._idcap.createDirectory(this.MEDIA_DIR)
      }
    } catch (err: any) {
      this.logService.error(`Could not init medias directory`)
    }
  }

  setAppVisibilityAndKeyControlMode(): void {
    if (this.USE_IDCAP) {
      return this._idcap.setIdcapMode()
    } else {
      return this._hcap.setHcapMode()
    }
  }

  setHotelMode(): Promise<void> {
    const hotelMode = {
      enable: 'on',
      settings: {
        powerOnStatus: 'stand_by',
        volume: {
          enable: 'off',
          settings: {
            startVolume: {
              enable: 'off',
              level: 0,
            },
            minimumVolume: 0,
            maximumVolume: 100,
          },
        },
        keyManagement: {
          enable: 'on',
          settings: {
            irOperation: 'normal',
            localKeyOperation: 'normal',
          },
        },
        limitedMode: {
          enable: 'on',
          settings: {
            setupMenu: 'off',
            programmeChange: 'off',
            menuDisplay: 'off',
            osdDisplay: 'on',
            systemProviderMode: 'off',
          },
        },
        dtvProgrammeUpdate: 'Manual',
        powerOnDefault: {
          enable: 'off',
          settings: {
            input: 'off',
            programme: '2',
            avSetting: 'off',
            aspectRatio: 'disable',
          },
        },
        auxSourceSetting: {
          enable: 'off',
          settings: [
            { av1: 'on' },
            { av2: 'on' },
            { av3: 'on' },
            { rgb: 'on' },
            { comp1: 'on' },
            { comp2: 'on' },
            { comp3: 'on' },
            { hdmi1: 'on' },
            { hdmi2: 'on' },
            { hdmi3: 'on' },
            { hdmi4: 'on' },
          ],
        },
        powerManagement: 'off',
        radioVideoBlank: {
          enable: 'off',
          settings: {
            startOfRadioProgramme: 1,
            countOfRadioProgramme: 1,
          },
        },
      },
    }
    if (this.USE_IDCAP) {
      return this._idcap.setHotelMode(hotelMode)
    } else {
      return this._hcap.setHotelMode(hotelMode)
    }
  }

  getHotelMode(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getHotelMode()
    } else {
      return this._hcap.getHotelMode()
    }
  }

  // The room number in which the TV is located
  // When the room number is changed, property ‘tv_name’ is changed to the same value as ‘room_number’.
  // [string] Max length 21
  async checkAndUpdateRoomNumber(value: string): Promise<boolean> {
    const roomNumber = await this.getStringProperty('room_number')
    const sanitizedValue = value.substring(0, 21)
    if (roomNumber !== sanitizedValue) {
      if (this.USE_IDCAP) {
        await this._idcap.setProperty('room_number', sanitizedValue)
      } else {
        await this._hcap.setProperty('room_number', sanitizedValue)
      }
      return true
    } else {
      return false
    }
  }

  // The TV name to be displayed at the remote (SSID of TV)
  // Must be changed after room_number
  // [string] Max length is 30
  async checkAndUpdateTVName(value: string): Promise<boolean> {
    const tvName = await this.getStringProperty('tv_name')
    const sanitizedValue = value.substring(0, 30)
    if (tvName !== sanitizedValue) {
      if (this.USE_IDCAP) {
        await this._idcap.setProperty('tv_name', sanitizedValue)
      } else {
        await this._hcap.setProperty('tv_name', sanitizedValue)
      }
      return true
    } else {
      return false
    }
  }

  getBrowserDebugMode(): Promise<boolean> {
    if (this.USE_IDCAP) {
      return this._idcap.getBrowserDebugMode()
    } else {
      return this._hcap.getBrowserDebugMode()
    }
  }

  setBrowserDebugMode(enable: boolean): void {
    if (this.USE_IDCAP) {
      return this._idcap.setBrowserDebugMode(enable)
    } else {
      return this._hcap.setBrowserDebugMode(enable)
    }
  }

  async checkAndUpdateDebugMode(enable: boolean): Promise<boolean> {
    const debugMode = await this.getBrowserDebugMode()
    if (debugMode !== enable) {
      this.setBrowserDebugMode(enable)
      return true
    } else {
      return false
    }
  }

  setDisplayResolution(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setDisplayResolution(value)
    } else {
      return this._hcap.setDisplayResolution(value)
    }
  }

  setTVChannelFloatingUI(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setTVChannelFloatingUI(value)
    } else {
      return this._hcap.setTVChannelFloatingUI(value)
    }
  }

  setBootSequenceOption(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setBootSequenceOption(value)
    } else {
      return this._hcap.setBootSequenceOption(value)
    }
  }

  // Set instant power-on mode (no default so we can change it manually)
  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/power-mode-and-instant-on
  configureInstantPowerOnMode(htvConfig: HTVConfig): void {
    if (htvConfig && htvConfig.webosConfig && htvConfig.webosConfig.instantOn) {
      let instantOn = null
      switch (htvConfig.webosConfig.instantOn) {
        case WebOSInstantOn.OFF:
          instantOn = '0'
          break
        case WebOSInstantOn.INSTANT_REBOOT:
          instantOn = '1'
          break
        case WebOSInstantOn.INSTANT_MUTE:
          instantOn = '2'
          break
        default:
          break
      }
      if (instantOn) {
        this.setInstantPowerOnMode(instantOn)
      }
    }
  }

  setInstantPowerOnMode(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setInstantPowerOnMode(value)
    } else {
      return this._hcap.setInstantPowerOnMode(value)
    }
  }

  setApplicationChannelControl(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setApplicationChannelControl(value)
    } else {
      return this._hcap.setApplicationChannelControl(value)
    }
  }

  setTvVolumeUI(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setTvVolumeUI(value)
    } else {
      return this._hcap.setTvVolumeUI(value)
    }
  }

  setDefaultSubtitleLanguage(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setDefaultSubtitleLanguage(value)
    } else {
      return this._hcap.setDefaultSubtitleLanguage(value)
    }
  }

  setDefaultAudioLanguage(value: string): void {
    if (this.USE_IDCAP) {
      return this._idcap.setDefaultAudioLanguage(value)
    } else {
      return this._hcap.setDefaultAudioLanguage(value)
    }
  }

  getSecurityLevel(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getStringProperty('security_level')
    } else {
      return this._hcap.getStringProperty('security_level')
    }
  }

  setSecurityLevel(value: string): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setSecurityLevel(value)
    } else {
      return this._hcap.setSecurityLevel(value)
    }
  }

  setStringProperty(key: string, value: string): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setProperty(key, value)
    } else {
      return this._hcap.setProperty(key, value)
    }
  }

  getStringProperty(key: string): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getStringProperty(key)
    } else {
      return this._hcap.getStringProperty(key)
    }
  }

  getModel(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getModel()
    } else {
      return this._hcap.getModel()
    }
  }

  getManufacturer(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getManufacturer()
    } else {
      return this._hcap.getManufacturer()
    }
  }

  getSerialNumber(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getSerialNumber()
    } else {
      return this._hcap.getSerialNumber()
    }
  }

  getFirmwareVersion(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getFirmwareVersion()
    } else {
      return this._hcap.getFirmwareVersion()
    }
  }

  getXaitVersion(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getXaitVersion()
    } else {
      return this._hcap.getXaitVersion()
    }
  }

  getCpuUsage(): Promise<number> {
    if (this.USE_IDCAP) {
      return this._idcap.getCpuUsage()
    } else {
      return this._hcap.getCpuUsage()
    }
  }

  getMemoryUsage(): Promise<MonitoringMemoryData> {
    if (this.USE_IDCAP) {
      return this._idcap.getMemoryUsage()
    } else {
      return this._hcap.getMemoryUsage()
    }
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getStorageData()
    } else {
      return this._hcap.getStorageData()
    }
  }

  reboot(): void {
    if (this.USE_IDCAP) {
      this._idcap.reboot()
    } else {
      this._hcap.reboot()
    }
  }

  getPowerState(): Promise<PowerState | undefined> {
    if (this.USE_IDCAP) {
      return this._idcap.getPowerState()
    } else {
      return this._hcap.getPowerState()
    }
  }

  setPowerState(state: PowerState): void {
    if (this.USE_IDCAP) {
      this._idcap.setPowerState(state)
    } else {
      this._hcap.setPowerState(state)
    }
  }

  getNetworkState(): Promise<NetworkState> {
    if (this.USE_IDCAP) {
      return this._idcap.getNetworkState()
    } else {
      return this._hcap.getNetworkState()
    }
  }

  getNetworkData(): Promise<NetworkData> {
    if (this.USE_IDCAP) {
      return this._idcap.getNetworkData()
    } else {
      return this._hcap.getNetworkData()
    }
  }

  getSoftAPConfiguration(): Promise<any> {
    if (this.USE_IDCAP) {
      return this._idcap.getSoftAPConfiguration()
    } else {
      return Promise.resolve()
    }
  }

  disableSoftAP(): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.disableSoftAP()
    } else {
      return Promise.resolve()
    }
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return this._idcap.listFiles(this.MEDIA_DIR)
  }

  async downloadMedia(media: Media): Promise<LocalMediaFile> {
    const filename = `${media.id}.${media.fileExtension}`
    const path = `${this.MEDIA_DIR}/${filename}`

    if (await this._idcap.fileExist(path)) {
      return this._idcap.getLocalMediaFile(media.id, media.fileExtension)
    } else {
      return new Promise((resolve, reject) => {
        const source = `${media.url}?ts=${new Date().getTime()}`
        const destination = `${this.MEDIA_DIR}/${filename}`

        const checkDownload = async (ticket: number) => {
          try {
            const downloaded = await this._idcap.checkDownload(ticket)
            if (downloaded) {
              const localMediaFile = await this._idcap.getLocalMediaFile(media.id, media.fileExtension)
              resolve(localMediaFile)
            } else {
              setTimeout(() => {
                checkDownload(ticket)
              }, 1000)
            }
          } catch (err: any) {
            reject()
          }
        }

        this._idcap
          .startDownload(source, destination)
          .then(ticket => {
            setTimeout(() => {
              checkDownload(ticket)
            }, 1000)
          })
          .catch(() => {
            return reject()
          })
      })
    }
  }

  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    const path = `${this.MEDIA_DIR}/${mediaFile.filename}`
    return this._idcap.removeFile(path)
  }

  getVolume(): Promise<number> {
    if (this.USE_IDCAP) {
      return this._idcap.getVolume()
    } else {
      return this._hcap.getVolume()
    }
  }

  setVolume(volume: number): Promise<number> {
    if (this.USE_IDCAP) {
      return this._idcap.setVolume(volume)
    } else {
      return this._hcap.setVolume(volume)
    }
  }

  getVolumeMute(): Promise<boolean> {
    if (this.USE_IDCAP) {
      return this._idcap.getVolumeMute()
    } else {
      return this._hcap.getVolumeMute()
    }
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    if (this.USE_IDCAP) {
      return this._idcap.setVolumeMute(muted)
    } else {
      return this._hcap.setVolumeMute(muted)
    }
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getExternalInputs()
    } else {
      return this._hcap.getExternalInputs()
    }
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    if (this.USE_IDCAP) {
      this._idcap.registerExternalInputListener(subject)
    } else {
      this._hcap.registerExternalInputListener(subject)
    }
  }

  setExternalInput(type: string, index: number): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setExternalInput(type, index)
    } else {
      return this._hcap.setExternalInput(type, index)
    }
  }

  setVideoSize(pos: ChannelPosition): Promise<void> {
    return this._idcap.setVideoSize(pos)
  }

  startupMediaAPI(): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.startupMediaAPI()
    } else {
      // Not supported yet
      return Promise.resolve()
    }
  }

  shutdownMediaAPI(): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.shutdownMediaAPI()
    } else {
      // Not supported yet
      return Promise.resolve()
    }
  }

  startBroadcastChannel(source: DVBTChannel): void {
    if (this.USE_IDCAP) {
      this._idcap.startBroadcastChannel(source)
    } else {
      this._hcap.startBroadcastChannel(source)
    }
  }

  startUDPChannel(source: UDPChannel): void {
    if (this.USE_IDCAP) {
      this._idcap.startUDPChannel(source)
    } else {
      this._hcap.startUDPChannel(source)
    }
  }

  startWebChannel(source: WebChannel): void {
    if (this.USE_IDCAP) {
      this._idcap.startWebChannel(source)
    } else {
      // Not supported yet
    }
  }

  stopChannel(): void {
    if (this.USE_IDCAP) {
      this._idcap.stopChannel()
    } else {
      this._hcap.stopChannel()
    }
  }

  stopWebChannel(): void {
    if (this.USE_IDCAP) {
      this._idcap.stopWebChannel()
    } else {
      // Not supported yet
    }
  }

  getCurrentChannel(): Promise<any> {
    if (this.USE_IDCAP) {
      return this._idcap.getCurrentChannel()
    } else {
      return this._hcap.getCurrentChannel()
    }
  }

  getAudioLanguageList(): Promise<string[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getAudioLanguageList()
    } else {
      return this._hcap.getAudioLanguageList()
    }
  }

  getAudioLanguageCurrentIndex(): Promise<number> {
    if (this.USE_IDCAP) {
      return this._idcap.getAudioLanguageCurrentIndex()
    } else {
      return this._hcap.getAudioLanguageCurrentIndex()
    }
  }

  setAudioLanguageCurrentIndex(index: number): void {
    if (this.USE_IDCAP) {
      this._idcap.setAudioLanguageCurrentIndex(index)
    } else {
      this._hcap.setAudioLanguageCurrentIndex(index)
    }
  }

  getSubtitleList(): Promise<string[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getSubtitleList()
    } else {
      return this._hcap.getSubtitleList()
    }
  }

  getSubtitleCurrentIndex(): Promise<number> {
    if (this.USE_IDCAP) {
      return this._idcap.getSubtitleCurrentIndex()
    } else {
      return this._hcap.getSubtitleCurrentIndex()
    }
  }

  setSubtitleCurrentIndex(index: number): void {
    if (this.USE_IDCAP) {
      return this._idcap.setSubtitleCurrentIndex(index)
    } else {
      return this._hcap.setSubtitleCurrentIndex(index)
    }
  }

  getMediaAudioLanguage(): Promise<{ index: number; list: string[] }> {
    if (this.USE_IDCAP) {
      return this._idcap.getMediaAudioLanguage()
    } else {
      // Not supported yet
      return Promise.reject()
    }
  }

  setMediaAudioLanguageIndex(index: number): void {
    if (this.USE_IDCAP) {
      this._idcap.setMediaAudioLanguageIndex(index)
    } else {
      // Not supported yet
    }
  }

  getMediaSubtitle(): Promise<{ type: string; index: number; list: string[] }> {
    if (this.USE_IDCAP) {
      return this._idcap.getMediaSubtitle()
    } else {
      // Not supported yet
      return Promise.reject()
    }
  }

  setMediaSubtitle(type: string, index: number): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setMediaSubtitle(type, index)
    } else {
      // Not supported yet
      return Promise.resolve()
    }
  }

  setMediaSubtitleOn(subtitleOn: boolean): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setMediaSubtitleOn(subtitleOn)
    } else {
      // Not supported yet
      return Promise.resolve()
    }
  }

  getAppList(): Promise<any[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getAppList()
    } else {
      return this._hcap.getAppList()
    }
  }

  startApp(appId: string, params: object, noSplash: boolean): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.startApp(appId, params, noSplash)
    } else {
      return this._hcap.startApp(appId, params)
    }
  }

  stopApp(appId: string): void {
    if (this.USE_IDCAP) {
      this._idcap.stopApp(appId)
    } else {
      this._hcap.stopApp(appId)
    }
  }

  installApps(appIds: string[]): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.installApps(appIds)
    } else {
      return this._hcap.installApps(appIds)
    }
  }

  uninstallApps(appIds: string[]): void {
    if (this.USE_IDCAP) {
      this._idcap.uninstallApps(appIds)
    } else {
      this._hcap.uninstallApps(appIds)
    }
  }

  getCurrentApp(): Promise<{ appId: string }> {
    return this._idcap.getCurrentApp()
  }

  registerAppToken(tokenList: { id: string; token: string }[]): Promise<void> {
    return this._idcap.registerAppToken(tokenList)
  }

  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    if (this.USE_IDCAP) {
      this._idcap.registerApplicationInstalledListener(subject)
    } else {
      this._hcap.registerApplicationInstalledListener(subject)
    }
  }

  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    if (this.USE_IDCAP) {
      this._idcap.registerApplicationFocusListener(subject)
    } else {
      this._hcap.registerApplicationFocusListener(subject)
    }
  }

  getLocale(): Promise<string> {
    if (this.USE_IDCAP) {
      return this._idcap.getLocale()
    } else {
      return this._hcap.getLocale()
    }
  }

  setLocale(specifier: string): Promise<void> {
    if (this.USE_IDCAP) {
      return this._idcap.setLocale(specifier)
    } else {
      return this._hcap.setLocale(specifier)
    }
  }

  getLocaleList(): Promise<string[]> {
    if (this.USE_IDCAP) {
      return this._idcap.getLocaleList()
    } else {
      return this._hcap.getLocaleList()
    }
  }

  registerChannelChangedListener(subject: BehaviorSubject<boolean>): void {
    if (this.USE_IDCAP) {
      this._idcap.registerChannelChangedListener(subject)
    } else {
      this._hcap.registerChannelChangedListener(subject)
    }
  }
}
